import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, IconButton, Collapse } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/system';

interface FormSectionPanelProps {
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  sx?: SxProps<Theme>;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const FormSectionPanel: React.FC<FormSectionPanelProps> = ({
  title,
  children,
  actions,
  sx = {},
  collapsible = false,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Sync internal state when controlled state changes
  useEffect(() => {
    if (controlledExpanded !== undefined) {
      setInternalExpanded(controlledExpanded);
    }
  }, [controlledExpanded]);

  // Use controlled state if provided, otherwise use internal state
  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    if (collapsible) {
      const newExpanded = !expanded;
      if (onExpandedChange) {
        onExpandedChange(newExpanded);
      } else {
        setInternalExpanded(newExpanded);
      }
    }
  };

  return (
    <Paper sx={{ mt: 2, overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        border: '1px solid #e5e7eb',
        borderRadius: '8px', ...sx }}>
      {/* Gray Header Panel */}
      <Box
        sx={{
          border: '1px solid #e5e7eb',
          backgroundColor: 'grey.100',
          borderBottom: '1px solid',
          borderColor: '#e5e7eb',
          px: 3,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: collapsible ? 'pointer' : 'default',
        }}
        onClick={handleToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {collapsible && (
            <IconButton
              size="small"
              sx={{
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '1.0rem',
              margin: 0,
            }}
          >
            {title}
          </Typography>
        </Box>
        {actions && (
          <Box onClick={(e) => e.stopPropagation()}>
            {actions}
          </Box>
        )}
      </Box>

      {/* Content Area */}
      {collapsible ? (
        <Collapse in={expanded} timeout="auto">
          <Box sx={{ px: 3, py: 2 }}>
            {children}
          </Box>
        </Collapse>
      ) : (
        <Box sx={{ px: 3, py: 2 }}>
          {children}
        </Box>
      )}
    </Paper>
  );
};

export default FormSectionPanel;